import { useAsyncFocusBlur } from '@progress/kendo-react-common'
import {
    SchedulerItem,
    SchedulerItemContent,
    SchedulerViewItemHandle,
    SchedulerViewItemProps,
} from '@progress/kendo-react-scheduler'
import { useAppContext } from 'context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import {
    createRef,
    RefObject,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { useBookingService } from 'services/booking'
import { normalizeUser } from 'services/user'
import { AttendeeItem } from 'types/attendee'
import { BookingForm, BookingFormExtra } from 'types/booking'
import { CalendarSlotItem } from 'types/calendar'
import { OrderApiOutput, OrderType } from 'types/order'
import { TenantIntegration } from 'types/tenant-integration'
import { addAlpha, classNames } from 'utils/dom-helpers'
import ItemSidebar from 'components/CalendarItem/ItemSidebar'
import { denormalizeOrder } from 'services/order'
import { getApiRequest } from 'utils/fetch-helper'

function getRect(el: HTMLElement | null) {
    const rect = { top: 0, left: 0, width: 0, height: 0 }

    if (el) {
        rect.top = el.offsetTop
        rect.left = el.offsetLeft
        rect.width = el.offsetWidth
        rect.height = el.offsetHeight
    }
    return rect
}

export interface GViewItemExtraProps {
    handleClick?(item: Record<string, string>): void
    handleDelete?(item: CalendarSlotItem): void
    handleEdit?(item: CalendarSlotItem): void
    onUpdate?(): void
}
export function GViewItem(
    props: SchedulerViewItemProps & GViewItemExtraProps
) {
    const {
        getGroupClassAvailabilityBookings,
        getGroupClassAvailabilityAttendees,
    } = useBookingService()
    const { translations } = useAppContext()
    const {
        staff: instructors,
        services,
        location_payment_integrations,
        payment_integrations: tenant_payment_integrations,
    } = useTenantContext()
    const item: RefObject<SchedulerViewItemHandle> = createRef()
    const [show, setShow] = useState<boolean>(false)
    const [attendees, setAttendees] = useState<AttendeeItem[]>([])
    const [instructor, setInstructor] = useState<Record<string, string>>()
    const [popup_anim_css, animatePopup] = useState<string>('bg-opacity-0')
    const [animation_css, toggleAnimation] =
        useState<string>('translate-x-full')
    const [width, setWidth] = useState<number>(0)
    const [data, setData] = useState<CalendarSlotItem>(props.dataItem)
    const [payment_integrations, setPaymentIntegrations] =
        useState<TenantIntegration[]>()

    const [booking_form, setBookingForm] = useState<
        BookingForm & BookingFormExtra
    >({
        availability_id: data.availability_id,
        appointment_id: data.appointment_id,
        booking_id: data.booking_id,
        location_id: data.location_id,
        order_type: OrderType.SERVICE,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        service_id: data.service_id,
        price: data.price,
        amount: data.price,
        end: data.end,
        start: data.start,
    })

    function reflow() {
        const first_slot =
            props.slots && props.slots.length ? props.slots[0] : null
        if (
            props.slots.length === 0 ||
            !first_slot ||
            !first_slot._ref.current
        ) {
            return
        }

        const element = item.current && item.current.element

        if (!element) return

        const primary_hex = props.dataItem.primary_hex
        element.style.backgroundColor = addAlpha(primary_hex, 0.2)
        element.style.borderLeft = `4px solid ${primary_hex}`
        element.style.color = `${primary_hex}`
    }

    useEffect(() => {
        reflow()
    }, [])

    const isAppointment = !!data?.appointment_id

    useEffect(() => {
        handleDefaultStatsAppointment()
    }, [isAppointment, props?.dataItem?.attendees?.length])

    const handleDefaultStatsAppointment = () => {
        if (isAppointment) {
            const { dataItem } = props
            const tempAttendees = dataItem?.attendees?.length || 0
            setData((prev) => ({
                ...prev,
                slots: {
                    available: prev.slots.max - tempAttendees,
                    max: prev.slots.max,
                    taken: tempAttendees,
                },
            }))
        }
    }

    const handleClick = useCallback(
        async (evt) => {
            if (booking_form && booking_form?.location_id) {
                if (
                    location_payment_integrations[booking_form.location_id]
                        .length > 0
                ) {
                    setPaymentIntegrations(
                        location_payment_integrations[
                            booking_form.location_id
                        ]
                    )
                } else if (tenant_payment_integrations.length > 0) {
                    setPaymentIntegrations(tenant_payment_integrations)
                }
            }

            if (data.appointment_id) {
                setShow(true)
                const [service] = services?.filter(
                    ({ id }) => id === props.dataItem.service_id
                )
                setData({
                    ...service,
                    ...props.dataItem,
                })

                const [staff] = instructors?.filter(
                    ({ id }) => props.dataItem.staff_id === id
                )
                setInstructor(staff?.user)
                toggleAnimation('')
                animatePopup('bg-opacity-70')
            }

            if (data.availability_id) {
                setShow(true)
                setData(props.dataItem)
                const { attendees, instructor } =
                    await getGroupClassAvailabilityAttendees(
                        data.availability_id
                    )
                toggleAnimation('')
                animatePopup('bg-opacity-70')
                setInstructor(normalizeUser(instructor.user))

                const bookings_output =
                    await getGroupClassAvailabilityBookings(
                        data.availability_id
                    )
                if (bookings_output.message) {
                    return
                }

                const denorm_attendees: AttendeeItem[] = attendees.map(
                    (attendee: AttendeeItem) => {
                        const [booking] = bookings_output.bookings.filter(
                            (booking: Record<string, string>) => {
                                return (
                                    attendee.booking_id ==
                                    booking.booking_id
                                )
                            }
                        )
                        return {
                            ...attendee,
                            order_id: booking.order_id,
                            notes: booking?.notes,
                            signed_in_at: booking?.signed_in_at,
                        }
                    }
                )

                setAttendees(denorm_attendees)

                if (props.onClick) props.onClick(evt)
            }
            if (data && data.booking_id) {
                const booking = await getApiRequest(
                    `/v1/bookings/${data.booking_id}`
                )
                const allAttendees: AttendeeItem[] =
                    data?.attendees?.map((item: any, index: number) => {
                        return {
                            order: denormalizeOrder(
                                data.order as OrderApiOutput
                            ),
                            user: { ...item?.user, id: item?.id } || {},
                            id: item.id,
                            paid: data?.order?.fullyPaid as boolean,
                            notes: booking?.notes,
                            signed_in_at: booking?.signedInAt,
                        }
                    }) || []

                setAttendees((prev: any) => [...(allAttendees as any)])
            }
        },
        [props]
    )

    const handleBlur = useCallback(
        (evt) => {
            // setShow(false)
            if (props.onBlur) props.onBlur(evt)
        },
        [props]
    )

    const { onFocus, onBlur } = useAsyncFocusBlur({
        onFocus: props.onFocus,
        onBlur: handleBlur,
    })

    return (
        <div className="bg-green-200">
            <SchedulerItem
                ref={item}
                {...props}
                className={classNames(
                    props.className || '',
                    'g-view-item rounded bg-green'
                )}
                onClick={handleClick}
                onBlur={onBlur}
                onFocus={onFocus}
            >
                <SchedulerItemContent>
                    <div
                        title={data?.service_name || ''}
                        style={{
                            filter: 'brightness(95%)',
                        }}
                        className="flex flex-col truncate text-ellipsis z-10 leading-tight cursor-pointer"
                    >
                        <div className="flex flex-wrap justify-between mb-0.5">
                            <span
                                className="inline-block tracking-[-0.03em] font-bold truncate w-[70%]"
                                title={data?.service_name}
                            >
                                {data?.service_name}
                            </span>
                            <span
                                className={classNames(
                                    'inline-block px-1',
                                    data.slots.available <= 0
                                        ? 'rounded-[3px]'
                                        : 'rounded-full'
                                )}
                                style={{
                                    backgroundColor: addAlpha(
                                        data.slots.available <= 0
                                            ? '#26292A'
                                            : data?.primary_hex || '',
                                        0.2
                                    ),
                                    border:
                                        data.slots.available <= 0
                                            ? '0'
                                            : `1px solid ${addAlpha(
                                                  data?.primary_hex || '',
                                                  0.4
                                              )}`,
                                    color: data?.primary_hex,
                                }}
                            >
                                {data.slots.available <= 0 ? (
                                    <small
                                        title={translations.full || 'full'}
                                        className="uppercase"
                                    >
                                        {translations.full || 'full'}
                                    </small>
                                ) : (
                                    <small
                                        title={` ${data.slots.taken}/${data.slots.max}`}
                                    >
                                        {data.slots.taken}/{data.slots.max}
                                    </small>
                                )}
                            </span>
                        </div>
                        {data.appointment_id && (
                            <small
                                className="inline-block truncate items-center font-medium"
                                title={data?.attendees?.[0]?.name as string}
                            >
                                <i className="feather-users mr-1" />
                                {data?.attendees && data.attendees[0]?.name}
                            </small>
                        )}
                        <small
                            className="inline-block truncate items-center font-medium"
                            title={data?.location_name}
                        >
                            <i className="feather-map-pin mr-1" />
                            {data?.location_name}
                        </small>
                        <small
                            className="inline-block truncate items-center font-medium"
                            title={data?.staff_name}
                        >
                            {data?.staff_photo ? (
                                <img
                                    src={data?.staff_photo}
                                    className="inline-block h-3 w-3 rounded-full mr-1"
                                    alt="Staff Photo"
                                />
                            ) : (
                                <i className="feather-user mr-1" />
                            )}
                            {data?.staff_name}
                        </small>
                    </div>
                </SchedulerItemContent>
            </SchedulerItem>
            <ItemSidebar
                {...props}
                show={show}
                toggleShow={setShow}
                anim_css={[popup_anim_css, animation_css]}
                setAnimCss={(anim_css: string[]) => {
                    animatePopup(anim_css[0])
                    toggleAnimation(anim_css[1])
                }}
                data={data}
                setData={setData}
                staff={instructor}
                setStaff={setInstructor}
                payment_integrations={payment_integrations}
                attendees={attendees}
                setAttendees={setAttendees}
                booking_form={booking_form}
                setBookingForm={setBookingForm}
                is_from_client_modal={false}
            />
        </div>
    )
}

export default GViewItem
